import React, { useState, useCallback } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";
import { SUMMARY_FIELDS } from "./Fields";

export default function ExportInactiveButton({ current, startDate, endDate, sums }) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const handleExport = useCallback(async () => {
    try {
      setIsExporting(true);
      setExportProgress(10);

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Your Company";
      workbook.created = new Date();

      const thin = { style: "thin", color: { argb: "FFCCCCCC" } };
      const borderAll = { top: thin, left: thin, bottom: thin, right: thin };
      const styles = {
        title: {
          font: { bold: true, size: 12, color: { argb: "FF2563EB" } },
          fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0F2FE" } },
          alignment: { horizontal: "center", vertical: "middle" },
          border: borderAll,
        },
        cell: (col) => ({
          border: borderAll,
          numFmt: col >= 5 ? "#,##0" : undefined,
          alignment:
            col >= 5
              ? { horizontal: "right", vertical: "middle" }
              : { horizontal: "left", vertical: "middle" },
          font: { size: 11 },
        }),
        sumRow: {
          font: { bold: true, size: 11, color: { argb: "FF059669" } },
          fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFECFDF5" } },
          alignment: { horizontal: "right", vertical: "middle" },
          border: borderAll,
          numFmtCols: [5, 6],
        },
      };

      setExportProgress(30);
      const ws = workbook.addWorksheet("รายงานลูกค้าห่างเหิน", {
        views: [{ state: "frozen", ySplit: 1 }],
      });

      // Title row
      const titleText = `รายงานลูกค้าห่างเหิน (${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")})`;
      const titleRow = ws.addRow([titleText]);
      titleRow.font = { bold: true, size: 14, color: { argb: "FF1E40AF" } };
      titleRow.alignment = { horizontal: "center" };
      ws.mergeCells(1, 1, 1, SUMMARY_FIELDS.length);

      ws.addRow([]);
      setExportProgress(40);

      // Header row
      const headerRow = ws.addRow(SUMMARY_FIELDS.map((f) => f.label));
      headerRow.eachCell((cell, idx) => {
        Object.assign(cell, styles.title, styles.cell(idx));
      });
      setExportProgress(50);

      // Data rows
      current.forEach((item, index) => {
        const row = ws.addRow(
          SUMMARY_FIELDS.map((f) => item[f.key])
        );
        row.eachCell((cell, idx) => {
          Object.assign(cell, styles.cell(idx));
        });
        if (index % 10 === 0) {
          setExportProgress(50 + (index / current.length) * 30);
        }
      });

      // Total summary row
      const totalRow = ws.addRow([
        'รวมทั้งหมด',
        '',
        '',
        '',
        '',
        sums.totalOrders,
        sums.totalSpent,
      ]);
      totalRow.eachCell((cell, idx) => Object.assign(cell, styles.sumRow));

      // Column widths
      ws.columns.forEach((col, i) => {
        const width = SUMMARY_FIELDS[i]?.w;
        col.width = width ? parseInt(width) * 1.2 : 15;
      });
      setExportProgress(90);

      // Write workbook and trigger download
      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = `Inactive_Customers_Report_${moment().format("YYYYMMDD_HHmmss")}.xlsx`;
      saveAs(
        new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
        fileName
      );

      setExportProgress(100);
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(0);
      }, 500);
    } catch (err) {
      setIsExporting(false);
      setExportProgress(0);
      console.error(err);
      alert(`เกิดข้อผิดพลาดในการส่งออก: ${err.message}`);
    }
  }, [current, startDate, endDate, sums]);

  return (
        <div className="d-flex justify-content-end align-items-center mb-3">
      {/* Export Progress */}
      {isExporting && (
        <div className="me-3" style={{ minWidth: "200px" }}>
          <div className="d-flex align-items-center">
            <div
              className="spinner-border spinner-border-sm text-success me-2"
              role="status"
            >
              <span className="visually-hidden">กำลังส่งออก...</span>
            </div>
            <div className="flex-grow-1">
              <div className="progress" style={{ height: "6px" }}>
                <div
                  className="progress-bar bg-gradient"
                  style={{
                    width: `${exportProgress}%`,
                    background: "linear-gradient(90deg, #10b981, #059669)",
                  }}
                ></div>
              </div>
              <small className="text-muted">
                {exportProgress.toFixed(0)}% กำลังส่งออก...
              </small>
            </div>
          </div>
        </div>
      )}

      {/* Export Button Group */}
      <div className="btn-group shadow-sm">
          <button
          type="button"
          className="btn btn-sm btn-outline-orange me-2 btn-export-dropdown  dropdown-toggle "
          data-bs-toggle="dropdown"
          disabled={isExporting}
        >
          <i className="fas fa-file-excel me-1"></i>
          Export Excel
        </button>

        <ul
          className="dropdown-menu dropdown-menu-end shadow-lg border-0"
          style={{ minWidth: "200px" }}
        >
          <li className="px-3 py-2 border-bottom">
            <small className="text-muted fw-semibold">
              เลือกรูปแบบการส่งออก
            </small>
          </li>
          <li>
            <button
              className="dropdown-item d-flex align-items-center py-2 px-3"
              onClick={() => handleExport("summary")}
              disabled={isExporting}
              style={{ transition: "all 0.2s ease" }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f3f4f6";
                e.target.style.paddingLeft = "20px";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "";
                e.target.style.paddingLeft = "12px";
              }}
            >
              <i className="fas fa-chart-bar text-success me-2"></i>
              <div>
                <div className="fw-medium">Export แบบสรุป</div>
                <small className="text-muted">สรุปยอดรวม</small>
              </div>
            </button>
          </li>
          <li>
            <hr className="dropdown-divider" />
          </li>
          <li className="px-3 py-2">
            <div className="d-flex align-items-center text-muted">
              <i className="fas fa-info-circle me-2"></i>
              <small>รวม {current?.length || 0} รายการ</small>
            </div>
          </li>
        </ul>
      </div>
    </div>
  );
}
